import { Injectable, computed, inject, signal } from '@angular/core';
import { WebviewService } from './webview.service';
import { WidgetService } from './widget.service';
import { ScriptLoaderService } from './script-loader.service';

export type AnalyticsConsent = 'granted' | 'denied';

/** localStorage key of the web visitor's choice. Storing the choice itself needs no consent. */
export const ANALYTICS_CONSENT_KEY = 'analyticsConsent';

/** Former key: it recorded an "OK" on an information-only banner, which is not consent, so it is dropped. */
const LEGACY_COOKIES_ACCEPTED_KEY = 'cookiesAccepted';

/**
 * Google Analytics properties. G-LM9NX4FNVL is GA4; UA-140218980-1 is Universal Analytics, which
 * Google retired in 2023-2024. Both are kept until it is decided which ones are still in use.
 */
export const GA_MEASUREMENT_IDS = ['G-LM9NX4FNVL', 'UA-140218980-1'];
const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_IDS[0]}`;

/** Cookies Google Analytics sets on the site's own domain. */
const ANALYTICS_COOKIE = /^(_ga|_gid|_gat)/;

/**
 * Google Analytics runs only with the visitor's prior opt-in (docs/cookie-consent.md):
 *
 * - web: the cookie banner asks; the choice is kept in localStorage and can be changed in the
 *   settings dialog. Until a choice is made, gtag.js is not loaded at all.
 * - GPST webview: the Android app asks for analytics consent itself, so the page never asks and
 *   analytics runs as before.
 * - widget: the embedding page owns consent and passes it with `analytics=1` or
 *   `window.evWidget.setAnalyticsConsent(true)`; the default is off and nothing is stored.
 *
 * Google Consent Mode v2 is set to `denied` for everything before the tag loads; on opt-in only
 * `analytics_storage` is granted (no ads). Withdrawing consent disables the tag
 * (`ga-disable-<id>`) and deletes the analytics cookies.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsConsentService {

  private readonly webviewService = inject(WebviewService);
  private readonly widgetService = inject(WidgetService);
  private readonly scriptLoader = inject(ScriptLoaderService);

  /** The current choice; null while a web visitor has not answered the banner yet. */
  public readonly consent = signal<AnalyticsConsent | null>(null);

  /** True on the web, where the visitor is asked; the app and the embedding page decide elsewhere. */
  public readonly asksVisitor = signal<boolean>(true);

  public readonly bannerVisible = computed(() => this.asksVisitor() && this.consent() === null);

  private initialized = false;
  private tagConfigured = false;

  public init(): void {
    if (this.initialized) return;
    this.initialized = true;

    localStorage.removeItem(LEGACY_COOKIES_ACCEPTED_KEY);
    this.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });

    if (this.widgetService.isWidget()) {
      this.asksVisitor.set(false);
      this.apply(this.widgetService.analyticsConsent() ? 'granted' : 'denied');
    } else if (this.webviewService.IsGPSTWebview()) {
      this.asksVisitor.set(false);
      this.apply('granted');
    } else {
      const stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
      this.apply(stored === 'granted' || stored === 'denied' ? stored : null);
    }
  }

  /** The visitor's (web) or the embedding page's (widget) choice. Only the web choice is stored. */
  public setConsent(granted: boolean): void {
    const consent: AnalyticsConsent = granted ? 'granted' : 'denied';
    if (this.asksVisitor()) {
      localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
    }
    this.apply(consent);
  }

  private apply(consent: AnalyticsConsent | null): void {
    this.consent.set(consent);
    const granted = consent === 'granted';
    for (const id of GA_MEASUREMENT_IDS) {
      (globalThis as any)[`ga-disable-${id}`] = !granted;
    }

    if (!granted) {
      if (this.tagConfigured) {
        this.gtag('consent', 'update', { analytics_storage: 'denied' });
      }
      deleteAnalyticsCookies(document);
      return;
    }

    this.gtag('consent', 'update', { analytics_storage: 'granted' });
    if (!this.tagConfigured) {
      this.tagConfigured = true;
      this.gtag('js', new Date());
      for (const id of GA_MEASUREMENT_IDS) {
        this.gtag('config', id);
      }
      this.scriptLoader.load(GTAG_SRC).catch(() => { });
    }
  }

  /** Queues a command for gtag.js, which only accepts `arguments` objects on the dataLayer. */
  private gtag(..._args: unknown[]): void {
    const w = globalThis as any;
    w.dataLayer = w.dataLayer || [];
    w.gtag = w.gtag || function () { w.dataLayer.push(arguments); };
    w.gtag(..._args);
  }
}

/** Expires the Google Analytics cookies on the page's host and every parent domain they may have been set on. */
export function deleteAnalyticsCookies(doc: Document): void {
  const names = doc.cookie.split(';').map((cookie) => cookie.split('=')[0].trim()).filter((name) => ANALYTICS_COOKIE.test(name));
  const labels = doc.location.hostname.split('.');
  const expired = '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  for (const name of names) {
    doc.cookie = name + expired;
    for (let i = 0; i < labels.length - 1; i++) {
      doc.cookie = `${name}${expired}; domain=.${labels.slice(i).join('.')}`;
    }
  }
}
